import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { ParentService, ParentDashboardData } from '../services/ParentService';
import { ParentSessionService, ClassAttendanceData } from '../services/ParentSessionService';
import { gameAPI } from '../../games/services/GameAPI';
import WeeklyClassCarousel from './WeeklyClassCarousel';
import FallAlbumSection from './FallAlbumSection';
import AddStudentModal from './AddStudentModal';
import ParentAccountSettingsModal from './ParentAccountSettingsModal';
import '../styles/ParentDashboard.css';

type ParentDashboardProps = {
  /**
   * Admin-only: view this dashboard for a specific parent without switching auth.
   * When provided, the dashboard becomes read-only (no add-student / settings).
   */
  adminParentIdOverride?: string;
};

const ParentDashboard: React.FC<ParentDashboardProps> = ({ adminParentIdOverride }) => {
  const { userData } = useAuth();
  const [dashboardData, setDashboardData] = useState<ParentDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDatesModalForChildId, setShowDatesModalForChildId] = useState<string | null>(null);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [sessionsByChild, setSessionsByChild] = useState<Record<string, ClassAttendanceData[]>>({});
  const [songsCountByChild, setSongsCountByChild] = useState<Record<string, number>>({});

  const effectiveParentId = adminParentIdOverride || userData?.id || null;
  const isAdminPreview = !!adminParentIdOverride;

  const loadDashboardData = useCallback(async () => {
    if (!effectiveParentId) return;

    setLoading(true);
    const data = await ParentService.getParentDashboardData(effectiveParentId);
    setDashboardData(data);

    // Fetch session history for each child
    if (data?.linkedChildren) {
      const sessionsData: Record<string, ClassAttendanceData[]> = {};
      const songsCountData: Record<string, number> = {};

      for (const childData of data.linkedChildren) {
        if (childData.squad?.id) {
          const sessions = await ParentSessionService.getRecentClassSessions(
            childData.squad.id,
            childData.child.id,
            5 // Get last 5 classes
          );
          sessionsData[childData.child.id] = sessions;
        }

        // Lightweight check: does this child have at least 1 saved song?
        try {
          const songs = await gameAPI.getUserSongs(childData.child.id, undefined, 1);
          songsCountData[childData.child.id] = songs.length;
        } catch (e) {
          console.warn('Error checking songs for child', childData.child.id, e);
          songsCountData[childData.child.id] = 0;
        }
      }

      setSessionsByChild(sessionsData);
      setSongsCountByChild(songsCountData);
    }

    setLoading(false);
  }, [effectiveParentId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleLaunchStudentAccount = async (childId: string, childName: string) => {
    try {
      await ParentService.launchStudentAccount(childId, childName);
    } catch (error) {
      console.error('Error launching student account from dashboard:', error);
    }
  };

  if (loading) {
    return (
      <div className="parent-dashboard">
        <div className="parent-dashboard-content">
          <div className="loading-state">
            <div className="loading-icon">🎵</div>
            <h2>Loading your family dashboard...</h2>
          </div>
        </div>
      </div>
    );
  }
  const hasLinkedChildren = ((dashboardData?.linkedChildren?.length) || 0) > 0;
  const anyChildHasClassesAndSongs = (dashboardData?.linkedChildren || []).some(c =>
    ((sessionsByChild[c.child.id]?.length) || 0) > 0 && ((songsCountByChild[c.child.id]) || 0) > 0
  );

  return (
    <div className="parent-dashboard">
      {/* Winter Promo Bar */}
      <div className="winter-promo-bar">
        <div className="winter-promo-content">
          <span className="winter-icon">❄️</span>
          <span className="winter-text">
            <strong>Blizzard Beats: Our Winter Program is Coming!</strong> New games, concepts and songs—same great format—👀 registration opens soon!
          </span>
        </div>
      </div>

      <div className="parent-dashboard-content">
        {/* Header */}
        <div className="parent-header compact">
          <div className="parent-header-top">
            <h1>Welcome to Overture</h1>
            <div className="header-actions-row">
              {!isAdminPreview && (
                <>
                  <button className="add-student-button" onClick={() => setShowAddStudentModal(true)}>
                    <span className="plus-icon">+</span> Add Student
                  </button>
                  <button className="parent-settings-button" onClick={() => setShowSettingsModal(true)}>
                    Account Settings
                  </button>
                </>
              )}
              {hasLinkedChildren && (
                <div className="chip-row toolbar">
                  <span className="chip">✅ Active & Connected</span>
                  <span className="chip">👀 View Progress</span>
                  <span className="chip">🏠 Play at Home</span>
                </div>
              )}
            </div>
          </div>
          {isAdminPreview && (
            <p className="parent-subheader" style={{ marginTop: 8 }}>
              Viewing this parent account as an admin (read-only preview).
            </p>
          )}
          {!hasLinkedChildren && (
            <p className="parent-subheader">
              Add your child using the orange button to get started! Then we'll link their account on the backend (this can take a few days). Thanks for creating an account!
            </p>
          )}
        </div>

        {/* Linked Children Cards */}
        {hasLinkedChildren && (
          <div className="children-section">
            <div className="children-grid">
              {dashboardData!.linkedChildren.map((childData) => {
                const squad = childData.squad;
                const teacher = childData.teacher;
                // Build summary pieces
                const timeRange = ParentService.formatScheduleTime(squad?.schedule?.startTime, squad?.schedule?.endTime);
                const mainDate = String(squad?.schedule?.mainDate || '').trim();
                const scheduleLabel = mainDate
                  ? new Date(`${mainDate}T00:00:00.000Z`).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC',
                  })
                  : squad?.schedule?.dayOfWeek
                    ? ParentService.formatDayOfWeek(squad.schedule.dayOfWeek, { plural: false })
                    : '';
                const metaLine = [
                  scheduleLabel,
                  timeRange && timeRange !== 'Time TBD' ? timeRange.replace(' - ', '–') : ''
                ].filter(Boolean).join(' ');
                const programDates = squad ? ParentService.generateProgramDates(squad) : [];
                const totalClasses = programDates.length;
                const shortDate = (d: string) => {
                  const dt = new Date(d);
                  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                };
                const exceptions = squad ? ParentService.getNoClassDates(squad) : [];
                const shortExceptions = exceptions.map((d) => shortDate(d));
                // Determine the next upcoming class date
                const today = new Date();
                const nextClass = programDates
                  .map(d => new Date(d))
                  .find(d => d.getTime() >= new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime());
                const nextClassShort = nextClass ? nextClass.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;

                // Build timeline entries: include scheduled dates (not canceled) and exceptions as canceled
                const scheduledSet = new Set(programDates.map(d => new Date(d).toDateString()));
                const canceledDates = (exceptions || []).map(d => new Date(d));
                const timelineAll: { date: Date; label: string; canceled: boolean; isNext: boolean }[] = [
                  ...programDates.map(d => new Date(d)).map(d => ({
                    date: d,
                    label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                    canceled: false,
                    isNext: nextClass ? d.getTime() === nextClass.getTime() : false,
                  })),
                  ...canceledDates
                    // Exclude any canceled date that is also in scheduled set (shouldn't happen but safe)
                    .filter(d => !scheduledSet.has(d.toDateString()))
                    .map(d => ({
                      date: d,
                      label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      canceled: true,
                      isNext: false,
                    }))
                ].sort((a, b) => a.date.getTime() - b.date.getTime());
                const scrollTimeline = (viewportId: string, dir: 'left' | 'right') => {
                  const el = document.getElementById(viewportId);
                  if (!el) return;
                  const delta = 180 * (dir === 'left' ? -1 : 1);
                  el.scrollBy({ left: delta, behavior: 'smooth' });
                };

                return (
                  <div key={childData.child.id} className="child-card compact">
                    <div className="child-header compressed">
                      <div className="child-left">
                        <div className="child-muse">
                          {childData.child.selectedMuse?.img ? (
                            <img
                              src={childData.child.selectedMuse.img}
                              alt={`${childData.child.displayName || 'Student'}'s Muse`}
                              className="muse-image"
                            />
                          ) : (
                            <div className="muse-placeholder">🎭</div>
                          )}
                        </div>
                        <div className="child-info">
                          <h3>{(childData.child.displayName || 'Student').split(' ')[0]}</h3>
                          {nextClassShort && (
                            <div className="next-class-hero" role="status">Next class: {nextClassShort}</div>
                          )}
                          {metaLine && (
                            <div className="meta-line">{metaLine}</div>
                          )}
                          <div className="header-actions">
                            <button
                              className="launch-button launch-button-compact"
                              onClick={() => handleLaunchStudentAccount(childData.child.id, childData.child.displayName || 'Student')}
                              aria-label={`Launch ${(childData.child.displayName || 'Student').split(' ')[0]}'s account`}
                            >
                              <span className="launch-icon">🚀</span>
                              {`Launch ${(childData.child.displayName || 'Student').split(' ')[0]}'s account`}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {squad && (
                      <div className="program-logistics compact">
                        <div className="info-grid">
                          <div className="info-row"><span className="info-label">School</span><span className="info-value">{squad.schoolName || squad.locationName || '—'}</span></div>
                          {teacher && (
                            <div className="info-row"><span className="info-label">Teacher</span><span className="info-value">{teacher.displayName}</span></div>
                          )}
                          {/* Avoid duplicating day/time/dates here; it's shown in meta line above */}
                        </div>
                        {/* Scrollable timeline */}
                        {timelineAll.length > 0 && (
                          <div className="schedule-summary">
                            <div className="summary-line">🗓 {totalClasses} {totalClasses === 1 ? 'class' : 'classes'}</div>
                            <div className="timeline">
                              <button className="timeline-arrow left" aria-label="Scroll left" onClick={() => scrollTimeline(`timeline-${childData.child.id}`, 'left')}>←</button>
                              <div className="timeline-viewport" id={`timeline-${childData.child.id}`}>
                                <div className="timeline-row">
                                  {timelineAll.map((t, i) => (
                                    <span
                                      key={i}
                                      className={
                                        `date-pill ${t.isNext ? 'next' : ''} ${t.canceled ? 'canceled' : ''}`
                                      }
                                    >
                                      {t.label}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <button className="timeline-arrow right" aria-label="Scroll right" onClick={() => scrollTimeline(`timeline-${childData.child.id}`, 'right')}>→</button>
                            </div>
                            {shortExceptions.length > 0 && (
                              <div className="exceptions-box" role="note"><span className="warn">⚠️</span> No class: {shortExceptions.join(', ')}</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Weekly Class Carousel - appears below program logistics */}
                    {squad && sessionsByChild[childData.child.id] && sessionsByChild[childData.child.id].length > 0 && (
                      <WeeklyClassCarousel
                        studentFirstName={(childData.child.displayName || 'Student').split(' ')[0]}
                        sessions={sessionsByChild[childData.child.id]}
                      />
                    )}

                    {/* Fall Album Section - appears below carousel */}
                    <FallAlbumSection
                      studentId={childData.child.id}
                      studentFirstName={(childData.child.displayName || 'Student').split(' ')[0]}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* What's coming next - hidden if student already has classes and songs */}
        {!anyChildHasClassesAndSongs && (
          <div className="coming-next-section">
            <h2>What's coming next</h2>
            <div className="feature-grid">
              <div className="feature-card">
                <div className="feature-icon">✨</div>
                <div className="feature-content">
                  <h4>Progress highlights</h4>
                  <p>Celebrate achievements and see key moments from class each week.</p>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🎶</div>
                <div className="feature-content">
                  <h4>Songs</h4>
                  <p>Listen to songs your child creates in class and share with family.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact Information */}
        <div className="contact-section">
          <h2>Questions? We're here to help!</h2>
          <div className="contact-grid">
            <div className="contact-card">
              <div className="contact-info">
                <h4>Aspen Buckingham</h4>
                <p className="contact-role">CEO</p>
                <p className="contact-details">
                  <a href="tel:360-391-3328">360-391-3328</a><br/>
                  <a href="mailto:aspen@overtureai.org">aspen@overtureai.org</a>
                </p>
              </div>
            </div>

            <div className="contact-card">
              <div className="contact-info">
                <h4>Steven Jiang</h4>
                <p className="contact-role">COO</p>
                <p className="contact-details">
                  <a href="mailto:steven@overtureai.org">steven@overtureai.org</a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Dates Modal */}
        {showDatesModalForChildId && (
          <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setShowDatesModalForChildId(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>All Class Dates</h3>
                <button className="modal-close" onClick={() => setShowDatesModalForChildId(null)} aria-label="Close">✕</button>
              </div>
              <div className="modal-body">
                {dashboardData?.linkedChildren.map((c) => {
                  if (c.child.id !== showDatesModalForChildId || !c.squad) return null;
                  const allDates = ParentService.generateProgramDates(c.squad);
                  return (
                    <div key={c.child.id} className="dates-list full">
                      {allDates.map((d, i) => (
                        <span key={i} className="date-chip">{d}</span>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Add Student Modal */}
        {!isAdminPreview && effectiveParentId && (
          <AddStudentModal
            isOpen={showAddStudentModal}
            onClose={() => setShowAddStudentModal(false)}
            onStudentAdded={loadDashboardData}
            parentId={effectiveParentId}
          />
        )}

        {/* Account Settings Modal */}
        {!isAdminPreview && dashboardData?.parent && (
          <ParentAccountSettingsModal
            isOpen={showSettingsModal}
            onClose={() => setShowSettingsModal(false)}
            parent={dashboardData.parent}
            linkedChildren={dashboardData.linkedChildren || []}
            onSaved={loadDashboardData}
          />
        )}
      </div>
    </div>
  );
};

export default ParentDashboard;
